import type { PickyResult } from "./picky-test";

export const PICKY_IMAGE_WIDTH = 1080;
export const PICKY_IMAGE_HEIGHT = 1350;

const PAPER = "#F0E2B4";
const ORANGE = "#F15A2B";
const GREEN = "#8AC53F";
const BLUE = "#4386B6";
const BODY_FONT = '"Arial Rounded MT Bold", ui-rounded, "PingFang SC", "Microsoft YaHei", sans-serif';
const HAND_FONT = '"Comic Sans MS", "Kaiti SC", STKaiti, cursive';

export interface PickyImageData {
  title: string;
  posterTitle: string;
  posterSubtitle: string;
  indexLabel: string;
  profileName: string;
  tags: readonly [string, string, string];
  description: string;
  focusLabel: string;
  selectionLabel: string;
  dateLabel: string;
}

export interface GeneratedPickyImage {
  blob: Blob;
  fileName: string;
  width: number;
  height: number;
}

export function buildPickyImageData(result: PickyResult, generatedAt = new Date()): PickyImageData {
  return {
    title: "我的挑食指数",
    posterTitle: "PICKY",
    posterSubtitle: "TASTE CLUB",
    indexLabel: String(result.index).padStart(2, "0"),
    profileName: result.profile.name,
    tags: result.profile.tags,
    description: result.profile.description,
    focusLabel: result.primaryPattern
      ? `最明显的忌口：${result.primaryPattern.profile.focusLabel}`
      : "没有明显的忌口类别",
    selectionLabel: `不吃 ${result.selectedCount} / ${result.totalCount} 种食材`,
    dateLabel: new Intl.DateTimeFormat("zh-CN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(generatedAt),
  };
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("图片生成失败，请再试一次"));
    }, "image/png");
  });
}

function setFont(
  context: CanvasRenderingContext2D,
  weight: number,
  size: number,
  family = BODY_FONT,
): void {
  context.font = `${weight} ${size}px ${family}`;
}

function wrapText(context: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = "";
  for (const character of Array.from(text)) {
    const candidate = line + character;
    if (line && context.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = character;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawWrappedText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines = 3,
): number {
  const lines = wrapText(context, text, maxWidth).slice(0, maxLines);
  lines.forEach((line, index) => context.fillText(line, x, y + (index * lineHeight)));
  return y + (lines.length * lineHeight);
}

function drawPaperTexture(context: CanvasRenderingContext2D): void {
  for (let index = 0; index < 520; index += 1) {
    const x = (index * 83) % PICKY_IMAGE_WIDTH;
    const y = (index * 137) % PICKY_IMAGE_HEIGHT;
    const size = (index % 3) + 1;
    context.fillStyle = index % 2 === 0 ? "rgba(67, 134, 182, 0.045)" : "rgba(241, 90, 43, 0.035)";
    context.fillRect(x, y, size, size);
  }
}

function drawLooseWord(
  context: CanvasRenderingContext2D,
  word: string,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  const rotations = [-0.045, 0.025, -0.02, 0.04, -0.015, 0.02];
  const offsets = [2, -5, 3, -2, 1, -3];
  let cursor = x;
  setFont(context, 900, size, BODY_FONT);
  context.fillStyle = color;
  Array.from(word).forEach((character, index) => {
    const width = context.measureText(character).width;
    context.save();
    context.translate(cursor + (width / 2), y + (offsets[index % offsets.length] ?? 0));
    context.rotate(rotations[index % rotations.length] ?? 0);
    context.fillText(character, -(width / 2), 0);
    context.restore();
    cursor += width * 0.91;
  });
}

function drawSparkle(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color = BLUE,
): void {
  context.save();
  context.strokeStyle = color;
  context.lineWidth = Math.max(4, size * 0.09);
  context.lineCap = "round";
  context.beginPath();
  context.moveTo(x, y - size);
  context.bezierCurveTo(x + 3, y - (size * 0.23), x + (size * 0.23), y - 3, x + size, y);
  context.bezierCurveTo(x + (size * 0.23), y + 3, x + 3, y + (size * 0.23), x, y + size);
  context.bezierCurveTo(x - 3, y + (size * 0.23), x - (size * 0.23), y + 3, x - size, y);
  context.bezierCurveTo(x - (size * 0.23), y - 3, x - 3, y - (size * 0.23), x, y - size);
  context.stroke();
  context.restore();
}

function drawDotCluster(context: CanvasRenderingContext2D, x: number, y: number, color = BLUE): void {
  context.fillStyle = color;
  const dots: ReadonlyArray<readonly [number, number, number]> = [[0, 0, 5], [30, -20, 3], [42, 16, 5], [-22, 26, 3]];
  dots.forEach(([offsetX, offsetY, radius]) => {
    context.beginPath();
    context.arc(x + offsetX, y + offsetY, radius, 0, Math.PI * 2);
    context.fill();
  });
}

function drawFlowerBadge(context: CanvasRenderingContext2D, x: number, y: number, radius: number, score: string): void {
  context.save();
  context.translate(x, y);
  context.fillStyle = ORANGE;
  for (let petal = 0; petal < 9; petal += 1) {
    context.save();
    context.rotate((petal / 9) * Math.PI * 2);
    context.beginPath();
    context.ellipse(0, -(radius * 0.72), radius * 0.48, radius * 0.7, 0, 0, Math.PI * 2);
    context.fill();
    context.restore();
  }
  context.beginPath();
  context.arc(0, 0, radius * 0.73, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = PAPER;
  context.textAlign = "center";
  setFont(context, 800, 24, HAND_FONT);
  context.fillText("挑食指数", 0, -25);
  setFont(context, 900, 63, BODY_FONT);
  context.fillText(score, -4, 41);
  setFont(context, 800, 25, BODY_FONT);
  context.fillText("/100", 54, 39);
  context.restore();
}

function drawChefBowl(context: CanvasRenderingContext2D, x: number, y: number, scale = 1): void {
  context.save();
  context.translate(x, y);
  context.scale(scale, scale);
  context.strokeStyle = BLUE;
  context.fillStyle = PAPER;
  context.lineWidth = 11;
  context.lineCap = "round";
  context.lineJoin = "round";

  context.beginPath();
  context.moveTo(-85, -115);
  context.bezierCurveTo(-132, -150, -110, -203, -66, -190);
  context.bezierCurveTo(-52, -232, 3, -226, 9, -185);
  context.bezierCurveTo(45, -214, 93, -177, 70, -135);
  context.bezierCurveTo(38, -111, -47, -104, -85, -115);
  context.fill();
  context.stroke();

  context.beginPath();
  context.moveTo(-130, -82);
  context.quadraticCurveTo(0, -42, 130, -82);
  context.lineTo(98, 54);
  context.quadraticCurveTo(0, 118, -98, 54);
  context.closePath();
  context.fill();
  context.stroke();

  context.beginPath();
  context.arc(-38, -8, 9, 0, Math.PI * 2);
  context.arc(38, -8, 9, 0, Math.PI * 2);
  context.fillStyle = BLUE;
  context.fill();
  context.beginPath();
  context.arc(0, 15, 34, 0.15, Math.PI - 0.15);
  context.stroke();

  context.beginPath();
  context.moveTo(-91, 16);
  context.bezierCurveTo(-168, 10, -170, -52, -215, -62);
  context.stroke();
  context.beginPath();
  context.moveTo(91, 14);
  context.bezierCurveTo(155, 5, 163, -55, 205, -87);
  context.stroke();

  context.beginPath();
  context.moveTo(205, -87);
  context.lineTo(234, -170);
  context.stroke();
  context.beginPath();
  context.ellipse(244, -195, 18, 33, 0.4, 0, Math.PI * 2);
  context.stroke();

  context.beginPath();
  context.moveTo(-47, 82);
  context.lineTo(-70, 160);
  context.moveTo(47, 82);
  context.lineTo(72, 160);
  context.stroke();
  context.beginPath();
  context.ellipse(-91, 170, 48, 15, -0.08, 0, Math.PI * 2);
  context.ellipse(92, 170, 48, 15, 0.08, 0, Math.PI * 2);
  context.fillStyle = BLUE;
  context.fill();

  context.beginPath();
  context.arc(-214, -69, 7, 0, Math.PI * 2);
  context.arc(-197, -60, 7, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

function drawServingBowl(context: CanvasRenderingContext2D, x: number, y: number, scale = 1): void {
  context.save();
  context.translate(x, y);
  context.scale(scale, scale);
  context.strokeStyle = BLUE;
  context.fillStyle = PAPER;
  context.lineWidth = 11;
  context.lineCap = "round";
  context.lineJoin = "round";
  context.beginPath();
  context.ellipse(0, 0, 225, 42, 0, 0, Math.PI * 2);
  context.fill();
  context.stroke();
  context.beginPath();
  context.moveTo(-215, 5);
  context.quadraticCurveTo(-185, 185, 0, 198);
  context.quadraticCurveTo(185, 185, 215, 5);
  context.stroke();
  context.beginPath();
  context.ellipse(0, 200, 132, 21, 0, 0, Math.PI * 2);
  context.fillStyle = BLUE;
  context.fill();
  context.beginPath();
  context.arc(-55, 95, 34, 0.1, Math.PI - 0.1);
  context.arc(55, 95, 34, 0.1, Math.PI - 0.1);
  context.stroke();
  context.restore();
}

function drawSectionTitle(context: CanvasRenderingContext2D, text: string, x: number, y: number): void {
  context.fillStyle = GREEN;
  setFont(context, 500, 48, HAND_FONT);
  context.fillText(text, x, y);
}

export async function generatePickyImage(
  result: PickyResult,
  generatedAt = new Date(),
): Promise<GeneratedPickyImage> {
  await document.fonts?.ready;
  const data = buildPickyImageData(result, generatedAt);
  const canvas = document.createElement("canvas");
  canvas.width = PICKY_IMAGE_WIDTH;
  canvas.height = PICKY_IMAGE_HEIGHT;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("图片生成失败，请再试一次");

  context.fillStyle = PAPER;
  context.fillRect(0, 0, PICKY_IMAGE_WIDTH, PICKY_IMAGE_HEIGHT);
  drawPaperTexture(context);

  drawLooseWord(context, data.posterTitle, 54, 168, 154, ORANGE);
  drawLooseWord(context, "TASTE", 502, 208, 77, GREEN);
  drawLooseWord(context, "CLUB", 668, 282, 69, GREEN);

  drawSparkle(context, 67, 54, 25);
  drawSparkle(context, 726, 80, 23);
  drawSparkle(context, 982, 124, 20);
  drawSparkle(context, 400, 248, 22);
  drawDotCluster(context, 344, 235);
  drawDotCluster(context, 960, 262);

  drawSectionTitle(context, "persona", 66, 342);
  context.fillStyle = ORANGE;
  setFont(context, 900, 48, BODY_FONT);
  let nextY = drawWrappedText(context, data.profileName, 68, 398, 320, 57, 2);
  setFont(context, 700, 25, BODY_FONT);
  nextY = drawWrappedText(context, data.description, 70, nextY + 14, 310, 38, 3);

  drawChefBowl(context, 520, 560, 1.03);
  drawFlowerBadge(context, 888, 410, 104, data.indexLabel);

  drawSectionTitle(context, "taste notes", 704, 650);
  context.fillStyle = ORANGE;
  setFont(context, 800, 30, BODY_FONT);
  data.tags.forEach((tag, index) => {
    context.fillText(tag, 718, 706 + (index * 50));
  });

  drawSectionTitle(context, "mostly avoid", 66, 730);
  context.fillStyle = ORANGE;
  setFont(context, 800, 29, BODY_FONT);
  const focusBottom = drawWrappedText(context, data.focusLabel, 72, 782, 310, 43, 3);
  setFont(context, 750, 25, BODY_FONT);
  context.fillText(data.selectionLabel, 72, focusBottom + 12);

  drawSparkle(context, 410, 818, 22);
  drawSparkle(context, 989, 800, 28);
  drawDotCluster(context, 958, 872);

  drawSectionTitle(context, "tested on", 655, 908);
  context.fillStyle = ORANGE;
  setFont(context, 800, 27, BODY_FONT);
  context.fillText(data.dateLabel, 662, 960);

  drawServingBowl(context, 287, 1095, 1.02);
  drawSparkle(context, 74, 1020, 26);
  drawSparkle(context, 590, 1042, 20);
  drawDotCluster(context, 614, 1116);

  context.save();
  context.fillStyle = BLUE;
  context.beginPath();
  context.moveTo(880, 1045);
  context.bezierCurveTo(995, 1000, 1072, 1022, 1080, 1115);
  context.lineTo(1080, 1350);
  context.lineTo(802, 1350);
  context.bezierCurveTo(828, 1284, 888, 1274, 845, 1214);
  context.bezierCurveTo(800, 1152, 806, 1085, 880, 1045);
  context.fill();
  context.restore();

  context.fillStyle = GREEN;
  setFont(context, 500, 42, HAND_FONT);
  context.fillText("come hungry", 540, 1110);
  context.fillStyle = ORANGE;
  setFont(context, 800, 25, BODY_FONT);
  drawWrappedText(context, "来我家前，先让我认识你的口味", 548, 1160, 235, 38, 2);

  context.fillStyle = ORANGE;
  setFont(context, 800, 20, BODY_FONT);
  context.fillText(data.title, 66, 1310);
  context.textAlign = "center";
  context.fillStyle = PAPER;
  setFont(context, 900, 22, BODY_FONT);
  context.fillText("design by 小羊", 956, 1310);

  return {
    blob: await canvasToBlob(canvas),
    fileName: `我的挑食指数-${result.profile.name}.png`,
    width: PICKY_IMAGE_WIDTH,
    height: PICKY_IMAGE_HEIGHT,
  };
}
